import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Button,
    InputAdornment,
    TextField,
    Grid,
    Stack,
    Avatar,
    Tooltip,
    Autocomplete,
    Drawer,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import "../../App.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { TableLoader } from "../../components/TableLoader";
import GroupIcon from "@mui/icons-material/Group";
import { useSnackbar } from "../../providers/SnackbarProvider";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomPagination from "../../components/CustomPagination";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { Delete, MessagesSquare } from "lucide-react";
import EventIcon from "@mui/icons-material/Event";
import { DeleteForever } from "@mui/icons-material";

const InterviewScheduler = () => {

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    const tableref = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, dashboard, instituteId, instituteName, TrainerName } =
        location.state || "";
    const [showTable, setShowTable] = useState(false);
    const [openDrawer, setOpen] = useState(false);
    const [appliedInstitute, setAppliedInstitute] = useState(
        instituteName
            ? instituteName
            : localStorage.getItem("applyInstitute")
                ? localStorage.getItem("applyInstitute")
                : null
    );
    const [appliedTrainer, setAppliedTrainer] = useState(
        TrainerName
            ? TrainerName
            : localStorage.getItem("applyTrainer")
                ? localStorage.getItem("applyTrainer")
                : null
    );
    const [selectedTrainerName, setSelectedTrainerName] = useState("");
    const [selectedCollegeName, setSelectedCollegeName] = useState("");

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const [searchKey, setSearchKey] = useState(
        localStorage.getItem("searchvalue") || ""
    );
    const debouncedSearch = useDebounce(searchKey);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [listData, setListData] = useState([]);
    const [selectedCollege, setSelectedCollege] = useState(
        instituteId
            ? instituteId
            : +localStorage.getItem("Institute_Id_Filter") ||
            +localStorage?.getItem("InstituteId") ||
            ""
    );
    const [colleges, setColleges] = useState([]);
    const [trainersList, setTrainersList] = useState([]);
    const [batchOptions, setBatchOptions] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [appliedBatch, setAppliedBatch] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(
        userId ? userId : +localStorage?.getItem("Trainer_Id_Filter") || ""
    );
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [interviewId, setInterviewId] = useState("")
    const [isDeleting, setIsDeleting] = useState(false)
    const [reason, setReason] = useState("")

    const { showSnackbar } = useSnackbar();
    // Pagination state
    const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
    const [rowsPerPage, setRowsPerPage] = useState(
        +localStorage.getItem("rowsPerPage") || 10
    );

    const [Trainers, setTrainers] = useState([]);

    const handleSearch = (e) => {
        localStorage.setItem("currentPage", 0);
        setPage(0);
        localStorage.setItem("searchvalue", e.target.value);
        setSearchKey(e.target.value);
    };

    const handleAddStudent = (isAdd, id) => {
        navigate("/add-interview-scheduler", {
            state: {
                addInterviewSchedule: isAdd,
                interviewId: id,
            },
        });
    };

    const handleClickClear = () => {
        tableref.current = {};
        setPage(0);
        // Only clear institute if SUPER_ADMIN, otherwise keep their institute
        if (localStorage.getItem("role") === "SUPER_ADMIN") {
            setSelectedCollege(null);
        } else {
            setSelectedCollege(+localStorage?.getItem("InstituteId") || "");
        }
        setSelectedTrainer(null);
        setAppliedTrainer(null);
        setAppliedInstitute(null);
        setSelectedBatch(null);
        setAppliedBatch(null);
        localStorage.removeItem("Institute_Id_Filter");
        localStorage.removeItem("applyInstitute");
        localStorage.removeItem("applyTrainer");
        localStorage.removeItem("Trainer_Id_Filter");
        localStorage.removeItem("applyBatch");

        navigate(location?.pathname, {
            replace: true,
            state: {
                ...location?.state,
                TrainerName: null,
                userId: null,
                instituteName: null,
                instituteId: null,
            },
        });

        // Trigger API call with cleared values
        setTimeout(() => studentList(), 0);
    };

    const handleApplyInstituteAndTrainer = () => {
        tableref.current = {};
        setPage(0);

        setAppliedInstitute(selectedCollegeName);
        localStorage.setItem("applyInstitute", selectedCollegeName);

        setAppliedTrainer(selectedTrainerName);
        localStorage.setItem("applyTrainer", selectedTrainerName);

        setAppliedBatch(selectedBatch);
        localStorage.setItem("applyBatch", selectedBatch ? JSON.stringify(selectedBatch) : "");

        setOpen(false); // close drawer after applying
    };

    const studentList = async () => {
        setLoading(true);

        const cachekey = `page_${page}_size_${rowsPerPage}_searchkey_${debouncedSearch}_selectedCollege_${selectedCollege}_trainer_${selectedTrainer}_batch_${appliedBatch?.batch_id}`;

        if (tableref.current[cachekey]) {
            const cached = tableref.current[cachekey];
            setListData(cached?.data);
            setCount(cached?.count);
            setLoading(false);
            return;
        }

        try {
            const payload = {
                page: page,
                size: rowsPerPage,
                search_key: debouncedSearch,
            };
            const res = await api.post("ListInterviews", payload);
            if (res?.data?.status === "success") {
                const data = res?.data?.data;
                const count = res?.data?.count;
                const trainer_names = res?.data?.data?.trainer_names;

                tableref.current[cachekey] = {
                    data,
                    count,
                    trainer_names,
                };

                setListData(res?.data?.data);
                setCount(res?.data?.count);
                setLoading(false);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.log("Error :", error);
            setLoading(false);
        }
    };

    const instituteList = async () => {
        try {
            const res = await api.post("/ListInstituteNames");
            if (res?.data?.status === "success") {
                setColleges(res?.data?.data);
            }
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Failed to fetch institutes.");
        }
    };

    const trainerList = async (id) => {
        try {
            const payload = {
                institute_id: id
                    ? id
                    : +localStorage?.getItem("InstituteId") || selectedCollege || 0,
            };
            const res = await api.post("/ListTrainersForFilter", payload);
            if (res?.data?.status === "success") {
                setTrainersList(res?.data?.data);
            }
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Failed to fetch institutes.");
        }
    };

    const batchList = async () => {
        try {
            const { data } = await api.post("getAllBatchList");
            setBatchOptions(data?.data ?? []);
        } catch (error) {
            console.error("Error fetching batch options:", error);
        }
    };

    useEffect(() => {
        studentList();
    }, [page, rowsPerPage, debouncedSearch, appliedInstitute, appliedTrainer, appliedBatch]);

    useEffect(() => {
        instituteList();
        batchList();
        if (selectedCollege) {
            trainerList();
        }
    }, []);

    // Load batch filter from localStorage on mount
    useEffect(() => {
        const savedBatch = localStorage.getItem("applyBatch");
        if (savedBatch) {
            setAppliedBatch(JSON.parse(savedBatch));
            setSelectedBatch(JSON.parse(savedBatch));
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTable(true);
        }, 200); // 0.2 sec delay

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        localStorage.setItem("currentpage", page);
        localStorage.setItem("rowsPerPage", rowsPerPage);
    }, [page, rowsPerPage]);

    const handleReasonChange = (e) => {
        const value = e.target.value
        setReason(value)
    }

    const handleDeleteInterview = async () => {
        try {
            if (!reason) {
                showSnackbar("error", "Please enter the reason before call off")
                return;
            }

            setIsDeleting(true)
            const response = await api.post("DeleteInterview", {
                interview_id: interviewId,
                deletion_reason: reason
            })
            if (response?.data?.status === "success") {
                setOpenDeleteDialog(false);
                showSnackbar("success", "Interview Cancelled Successfully")
                studentList()
            } else {
                showSnackbar("error", "Unable to cancel the interview")
            }
        } catch (err) {
            showSnackbar("error", "Failed to Call off the interview")
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        fontSize: "18px",
                        fontWeight: 600,
                        pb: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            backgroundColor: "#FFF0F0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <DeleteForever
                            sx={{
                                color: "#FF5C5C",
                                fontSize: 24,
                            }}
                        />
                    </Box>

                    Call Off Interview
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: "#666",
                            lineHeight: 1.6,
                        }}
                    >
                        Are you sure you want to cancel the interview?
                    </Typography>

                    <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                            Reason
                            <span style={{ color: "red" }}>
                                *
                            </span>
                        </Typography>
                        <TextField
                            value={reason}
                            onChange={handleReasonChange}
                            multiline
                            fullWidth
                            placeholder="Enter your reason"
                            minRows={2}
                            maxRows={3}
                            sx={{
                                "&. MuiInputBase-input": {
                                    fontSize: '12px'
                                },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                },

                                "& .MuiInputBase-input": {
                                    fontSize: "14px",
                                },

                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: "14px",
                                    opacity: 1,
                                    color: '#a2a2a2'
                                },
                                mt: 0.5
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 2,
                        pb: 2,
                        gap: 1,
                    }}
                >
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        variant="outlined"
                        disabled={isDeleting}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            color: "#555",
                            borderColor: "#DADADA",
                            "&:hover": {
                                borderColor: "#BDBDBD",
                                backgroundColor: "#F8F8F8",
                            },
                        }}
                    >
                        Close
                    </Button>

                    <Button
                        onClick={handleDeleteInterview}
                        variant="contained"
                        startIcon={
                            isDeleting ? (
                                <CircularProgress
                                    size={18}
                                    sx={{ color: "#a2a2a2" }}
                                />
                            ) : (
                                <DeleteForever sx={{ fontSize: 19 }} />
                            )
                        }
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            backgroundColor: "#FF5C5C",
                            boxShadow: "none",
                            "&:hover": {
                                backgroundColor: "#E94B4B",
                                boxShadow: "none",
                            },
                        }}
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Cancelling interview..." : "Call Off Interview"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                sx={{
                    // borderBottom: "1px solid #828487",
                    pb: 2,
                    width: '100%'
                }}
            >
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 0.5,
                        alignItems: "center",
                    }}
                >
                    <Box className="icon-box">
                        <EventIcon style={{ width: '32px', height: '32px', color: '#1e88e5' }} />
                    </Box>
                    {/* Title */}
                    <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a", marginLeft: 1 }}>
                        Interview Schedules
                    </Typography>
                </Grid>
                {/* Search + Button */}
                <Box display="flex" gap={4} alignItems="center">
                    <TextField
                        value={searchKey}
                        onChange={(e) => handleSearch(e)}
                        size="small"
                        variant="outlined"
                        placeholder={` Search by candidate and interviewer`}
                        className="custom-search-field"
                        sx={{ width: '100%', minWidth: '300px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {(localStorage.getItem("role") === "TRAINER" ||
                        localStorage.getItem("role") === "INSTITUTE_ADMIN") && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                className="gradient-button"
                                onClick={() => handleAddStudent("true", null)}
                                sx={{ width: '100%' }}
                            >
                                Schedule Interview
                            </Button>
                        )}
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    mt: 3,
                    opacity: showTable ? 1 : 0,
                    transform: showTable ? "translateY(0)" : "translateY(40px)",
                    transition: "opacity 0.5s ease, transform 0.5s ease",
                }}
            >
                <Table>
                    <TableHead>
                        {localStorage?.getItem("role") !== "TRAINER" && (
                            <TableRow>
                                <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : 8}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {appliedInstitute && appliedInstitute !== "null" && (
                                                <Box className="filter-chip">
                                                    <Typography
                                                        sx={{ fontSize: "13px", fontWeight: 500 }}
                                                    >
                                                        {instituteName
                                                            ? instituteName
                                                            : localStorage.getItem("applyInstitute")}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setAppliedInstitute(null);
                                                            setSelectedCollegeName(null);
                                                            setSelectedCollege(null);
                                                            setPage(0);
                                                            localStorage.removeItem("applyInstitute");
                                                            localStorage.removeItem("Institute_Id_Filter");
                                                            navigate(location.pathname, {
                                                                replace: true,
                                                                state: {
                                                                    ...location.state,
                                                                    instituteName: null,
                                                                    instituteId: null,
                                                                },
                                                            });
                                                        }}
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: "14px" }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            {appliedTrainer && appliedTrainer !== "null" && (
                                                <Box className="filter-chip">
                                                    <Typography
                                                        sx={{ fontSize: "13px", fontWeight: 500 }}
                                                    >
                                                        {localStorage.getItem("applyTrainer") ||
                                                            appliedTrainer}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setAppliedTrainer(null);
                                                            setSelectedTrainerName(null);
                                                            setSelectedTrainer(null);
                                                            setPage(0);
                                                            localStorage.removeItem("applyTrainer");
                                                            localStorage.removeItem("Trainer_Id_Filter");
                                                            navigate(location?.pathname, {
                                                                replace: true,
                                                                state: {
                                                                    ...location?.state,
                                                                    TrainerName: null,
                                                                    userId: null,
                                                                },
                                                            });
                                                        }}
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: "14px" }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            {appliedBatch && (
                                                <Box className="filter-chip">
                                                    <Typography
                                                        sx={{ fontSize: "13px", fontWeight: 500 }}
                                                    >
                                                        {appliedBatch?.batch_name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setAppliedBatch(null);
                                                            setSelectedBatch(null);
                                                            setPage(0);
                                                            localStorage.removeItem("applyBatch");
                                                        }}
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: "14px" }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <IconButton
                                                onClick={() => setOpen(true)}
                                                color="primary"
                                                className="more-filter-btn"
                                            >
                                                <FilterListIcon />
                                                <Typography className="more-filter-text">
                                                    More filter
                                                </Typography>
                                            </IconButton>

                                            {(appliedInstitute !== null ||
                                                appliedTrainer !== null ||
                                                appliedBatch !== null) && (
                                                    <Button
                                                        variant="outlined"
                                                        className="clear-filter-btn"
                                                        onClick={handleClickClear}
                                                    >
                                                        Clear Filter
                                                    </Button>
                                                )}
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow className="tableHeader">
                            <TableCell className="tableHeader">
                                <b>S.No</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Job Role</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Candidate</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Interviewer</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Schedule Date</b>
                            </TableCell>

                            <TableCell className="tableHeader">
                                <b>Status</b>
                            </TableCell>

                            <TableCell className="tableHeader">
                                <b>Actions</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {loading ? (
                        <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                            <TableLoader message="Fetching students..." />
                        </TableCell>
                    ) : (
                        <TableBody className="tablebody">
                            {listData.map((item, index) => (
                                <TableRow
                                    key={item.assessment_id}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                                        "&:hover": { backgroundColor: "#f1f1f1" },
                                    }}
                                >
                                    <TableCell align="center">
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item?.program_name || "-"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item.candidate?.first_name + " " + item.candidate?.last_name}
                                        </span>
                                        <br />
                                        {item?.candidate?.email}
                                    </TableCell>
                                    <TableCell>
                                        {item?.interviewer?.first_name + " " + item.interviewer?.last_name}
                                        <br />
                                        {item?.interviewer?.email}
                                    </TableCell>

                                    {/* <TableCell>{item.created_by}</TableCell> */}
                                    <TableCell sx={{ textWrap: "nowrap" }}>
                                        {item?.scheduled_at
                                            ? dayjs(item?.scheduled_at)?.format("DD-MM-YYYY, hh:mm A")
                                            : "-"}
                                    </TableCell>

                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "inline-block",
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: "12px",
                                                fontWeight: "bold",
                                                fontSize: "12px",
                                                backgroundColor:
                                                    item.status === "ACTIVE"
                                                        ? "#d4edda"
                                                        : item.status === "INACTIVE"
                                                            ? "#f8d7da"
                                                            : "#f1f1f1",
                                                color:
                                                    item.status === "ACTIVE"
                                                        ? "#155724"
                                                        : item.status === "INACTIVE"
                                                            ? "#721c24"
                                                            : "#333",
                                                textAlign: "center",
                                            }}
                                        >
                                            {item?.interviewer_response_status?.toLowerCase() === 'rejected' ? item?.interviewer_response_status : item.status}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            display="flex"
                                            justifyContent="left"
                                            alignItems="center"
                                            gap={0.5}
                                        >
                                            {/* View Button */}
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigate("/view-interview-scheduler", {
                                                        state: {
                                                            addInterviewSchedule: "false",
                                                            interviewId: item?.interview_id,
                                                        },
                                                    });
                                                    handleClose();
                                                }}
                                                sx={{
                                                    display: "flex",
                                                    // alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <VisibilityIcon color="primary" />
                                                <Typography color="primary" sx={{ fontSize: "12px" }}>
                                                    View
                                                </Typography>
                                            </IconButton>

                                            {/* Call off Button */}
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setOpenDeleteDialog(true);
                                                    setInterviewId(item?.interview_id)
                                                }}
                                                sx={{
                                                    display: "flex",
                                                    // alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <DeleteForever style={{ color: '#FF5C5C', fontSize: '20px' }} />
                                                <Typography sx={{ fontSize: "12px", color: '#FF5C5C' }}>
                                                    Call Off
                                                </Typography>
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {((page === 0 && listData?.length > rowsPerPage - 1) ||
                                page !== 0) && (
                                    <CustomPagination
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        count={count}
                                        setPage={setPage}
                                        colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8}
                                    />
                                )}

                            {listData?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                                        <TableNodata />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </Box>
    );
};

export default InterviewScheduler;
